import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Gift, Star, Stamp, Percent, Loader2, Plus, Edit, Trash2,
  ToggleLeft, ToggleRight, Ticket, Tag, Truck, ShoppingBag,
  Copy, CheckCircle2, X, Crown, Award, CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

interface LoyaltyProgram {
  id: string;
  restaurant_id: string;
  type: "points" | "stamp" | "cashback";
  is_active: boolean;
  name: string;
  description: string | null;
  points_per_real: number;
  points_to_redeem: number;
  reward_value: number;
  stamps_required: number;
  stamp_reward_description: string;
  cashback_percentage: number;
  min_order_value: number;
}

interface Coupon {
  id: string;
  restaurant_id: string;
  code: string;
  type: "percentage" | "fixed" | "free_shipping" | "free_product";
  value: number;
  product_id: string | null;
  min_order_value: number;
  max_uses: number | null;
  uses_count: number;
  max_uses_per_customer: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  description: string | null;
}

const LOYALTY_ICONS = {
  points: Star,
  stamp: Stamp,
  cashback: CreditCard,
};

const LOYALTY_LABELS = {
  points: "Programa de Pontos",
  stamp: "Cartão Fidelidade (Carimbos)",
  cashback: "Cashback",
};

const COUPON_TYPE_LABELS: Record<string, { label: string; icon: typeof Percent }> = {
  percentage: { label: "Percentual (%)", icon: Percent },
  fixed: { label: "Valor fixo (R$)", icon: Tag },
  free_shipping: { label: "Frete grátis", icon: Truck },
  free_product: { label: "Produto grátis", icon: ShoppingBag },
};

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const Fidelidade = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [saving, setSaving] = useState(false);

  // Loyalty dialog
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<LoyaltyProgram | null>(null);
  const [lpType, setLpType] = useState<"points" | "stamp" | "cashback">("points");
  const [lpName, setLpName] = useState("");
  const [lpDesc, setLpDesc] = useState("");
  const [lpPointsPerReal, setLpPointsPerReal] = useState(1);
  const [lpPointsToRedeem, setLpPointsToRedeem] = useState(100);
  const [lpRewardValue, setLpRewardValue] = useState(10);
  const [lpStampsRequired, setLpStampsRequired] = useState(10);
  const [lpStampReward, setLpStampReward] = useState("Produto grátis");
  const [lpCashbackPct, setLpCashbackPct] = useState(5);
  const [lpMinOrder, setLpMinOrder] = useState(0);

  // Coupon dialog
  const [couponOpen, setCouponOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [cpCode, setCpCode] = useState("");
  const [cpType, setCpType] = useState<"percentage" | "fixed" | "free_shipping" | "free_product">("percentage");
  const [cpValue, setCpValue] = useState(0);
  const [cpProductId, setCpProductId] = useState("");
  const [cpMinOrder, setCpMinOrder] = useState(0);
  const [cpMaxUses, setCpMaxUses] = useState<string>("");
  const [cpMaxPerCustomer, setCpMaxPerCustomer] = useState(1);
  const [cpValidUntil, setCpValidUntil] = useState("");
  const [cpDesc, setCpDesc] = useState("");
  const [cpAppliesToDiscounted, setCpAppliesToDiscounted] = useState(true);

  const fetchData = useCallback(async (restId: string) => {
    const [{ data: progs }, { data: coups }, { data: prods }] = await Promise.all([
      supabase.from("loyalty_programs").select("*").eq("restaurant_id", restId).order("created_at"),
      supabase.from("coupons").select("*").eq("restaurant_id", restId).order("created_at", { ascending: false }),
      supabase.from("products").select("*").eq("restaurant_id", restId),
    ]);
    setPrograms((progs as any[]) || []);
    setCoupons((coups as any[]) || []);
    setProducts(prods || []);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await fetchData(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  // Loyalty CRUD
  const openNewLoyalty = () => {
    setEditingProgram(null);
    setLpType("points");
    setLpName("");
    setLpDesc("");
    setLpPointsPerReal(1);
    setLpPointsToRedeem(100);
    setLpRewardValue(10);
    setLpStampsRequired(10);
    setLpStampReward("Produto grátis");
    setLpCashbackPct(5);
    setLpMinOrder(0);
    setLoyaltyOpen(true);
  };

  const openEditLoyalty = (p: LoyaltyProgram) => {
    setEditingProgram(p);
    setLpType(p.type);
    setLpName(p.name);
    setLpDesc(p.description || "");
    setLpPointsPerReal(p.points_per_real);
    setLpPointsToRedeem(p.points_to_redeem);
    setLpRewardValue(p.reward_value);
    setLpStampsRequired(p.stamps_required);
    setLpStampReward(p.stamp_reward_description);
    setLpCashbackPct(p.cashback_percentage);
    setLpMinOrder(p.min_order_value);
    setLoyaltyOpen(true);
  };

  const saveLoyalty = async () => {
    if (!restaurantId || !lpName.trim()) return;
    setSaving(true);
    try {
      const data = {
        restaurant_id: restaurantId,
        type: lpType,
        name: lpName.trim(),
        description: lpDesc || null,
        points_per_real: lpPointsPerReal,
        points_to_redeem: lpPointsToRedeem,
        reward_value: lpRewardValue,
        stamps_required: lpStampsRequired,
        stamp_reward_description: lpStampReward,
        cashback_percentage: lpCashbackPct,
        min_order_value: lpMinOrder,
      };
      if (editingProgram) {
        const { error } = await supabase.from("loyalty_programs").update(data).eq("id", editingProgram.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("loyalty_programs").insert(data);
        if (error) throw error;
      }
      toast({ title: editingProgram ? "Programa atualizado!" : "Programa criado!" });
      setLoyaltyOpen(false);
      await fetchData(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleLoyalty = async (p: LoyaltyProgram) => {
    if (!restaurantId) return;
    await supabase.from("loyalty_programs").update({ is_active: !p.is_active }).eq("id", p.id);
    await fetchData(restaurantId);
  };

  const deleteLoyalty = async (id: string) => {
    if (!restaurantId) return;
    await supabase.from("loyalty_programs").delete().eq("id", id);
    toast({ title: "Programa excluído!" });
    await fetchData(restaurantId);
  };

  // Coupon CRUD
  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const openNewCoupon = () => {
    setEditingCoupon(null);
    setCpCode(generateCode());
    setCpType("percentage");
    setCpValue(10);
    setCpProductId("");
    setCpMinOrder(0);
    setCpMaxUses("");
    setCpMaxPerCustomer(1);
    setCpValidUntil("");
    setCpDesc("");
    setCpAppliesToDiscounted(true);
    setCouponOpen(true);
  };

  const openEditCoupon = (c: Coupon) => {
    setEditingCoupon(c);
    setCpCode(c.code);
    setCpType(c.type);
    setCpValue(c.value);
    setCpProductId(c.product_id || "");
    setCpMinOrder(c.min_order_value);
    setCpMaxUses(c.max_uses?.toString() || "");
    setCpMaxPerCustomer(c.max_uses_per_customer);
    setCpValidUntil(c.valid_until ? c.valid_until.slice(0, 10) : "");
    setCpDesc(c.description || "");
    setCpAppliesToDiscounted((c as any).applies_to_discounted !== false);
    setCouponOpen(true);
  };

  const saveCoupon = async () => {
    if (!restaurantId || !cpCode.trim()) return;
    setSaving(true);
    try {
      const data: any = {
        restaurant_id: restaurantId,
        code: cpCode.trim().toUpperCase(),
        type: cpType,
        value: cpType === "free_shipping" ? 0 : cpValue,
        product_id: cpType === "free_product" && cpProductId ? cpProductId : null,
        min_order_value: cpMinOrder,
        max_uses: cpMaxUses ? parseInt(cpMaxUses) : null,
        max_uses_per_customer: cpMaxPerCustomer,
        valid_until: cpValidUntil ? new Date(cpValidUntil + "T23:59:59").toISOString() : null,
        description: cpDesc || null,
        applies_to_discounted: cpAppliesToDiscounted,
      };
      if (editingCoupon) {
        const { error } = await supabase.from("coupons").update(data).eq("id", editingCoupon.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("coupons").insert(data);
        if (error) throw error;
      }
      toast({ title: editingCoupon ? "Cupom atualizado!" : "Cupom criado!" });
      setCouponOpen(false);
      await fetchData(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleCoupon = async (c: Coupon) => {
    if (!restaurantId) return;
    await supabase.from("coupons").update({ is_active: !c.is_active }).eq("id", c.id);
    await fetchData(restaurantId);
  };

  const deleteCoupon = async (id: string) => {
    if (!restaurantId) return;
    await supabase.from("coupons").delete().eq("id", id);
    toast({ title: "Cupom excluído!" });
    await fetchData(restaurantId);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Código copiado!" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Gift className="h-5 w-5 text-primary" />
            <h1 className="font-display font-bold text-lg">Fidelidade & Cupons</h1>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5">
        <Tabs defaultValue="loyalty" className="space-y-5">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="loyalty" className="gap-2">
              <Crown className="h-4 w-4" /> Fidelidade
            </TabsTrigger>
            <TabsTrigger value="coupons" className="gap-2">
              <Ticket className="h-4 w-4" /> Cupons
            </TabsTrigger>
          </TabsList>

          {/* LOYALTY TAB */}
          <TabsContent value="loyalty" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display font-semibold">Programas de Fidelidade</h2>
                <p className="text-xs text-muted-foreground">Configure pontos, carimbos ou cashback para fidelizar seus clientes</p>
              </div>
              <Button onClick={openNewLoyalty} className="bg-gradient-primary text-primary-foreground gap-2">
                <Plus className="h-4 w-4" /> Novo Programa
              </Button>
            </div>

            {programs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-gradient-card p-12 text-center">
                <Award className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">Nenhum programa de fidelidade</p>
                <p className="text-xs text-muted-foreground mt-1">Crie um programa para incentivar seus clientes a voltarem</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {programs.map((p) => {
                  const Icon = LOYALTY_ICONS[p.type];
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-xl border bg-gradient-card shadow-card p-5 transition-all ${
                        p.is_active ? "border-primary/30" : "border-border opacity-60"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                            p.is_active ? "bg-primary/10" : "bg-secondary"
                          }`}>
                            <Icon className={`h-6 w-6 ${p.is_active ? "text-primary" : "text-muted-foreground"}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{p.name}</h3>
                              <Badge variant={p.is_active ? "default" : "secondary"} className="text-[10px]">
                                {p.is_active ? "Ativo" : "Inativo"}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{LOYALTY_LABELS[p.type]}</p>
                            {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}

                            {/* Type-specific details */}
                            <div className="flex flex-wrap gap-3 mt-3 text-xs">
                              {p.type === "points" && (
                                <>
                                  <span className="bg-secondary px-2 py-1 rounded">
                                    {p.points_per_real} pt/R$
                                  </span>
                                  <span className="bg-secondary px-2 py-1 rounded">
                                    {p.points_to_redeem} pts = {formatCurrency(p.reward_value)} desconto
                                  </span>
                                </>
                              )}
                              {p.type === "stamp" && (
                                <span className="bg-secondary px-2 py-1 rounded">
                                  {p.stamps_required} carimbos → {p.stamp_reward_description}
                                </span>
                              )}
                              {p.type === "cashback" && (
                                <>
                                  <span className="bg-secondary px-2 py-1 rounded">
                                    {p.cashback_percentage}% de volta
                                  </span>
                                  {p.min_order_value > 0 && (
                                    <span className="bg-secondary px-2 py-1 rounded">
                                      Pedido mín. {formatCurrency(p.min_order_value)}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => toggleLoyalty(p)}>
                            {p.is_active ? <ToggleRight className="h-5 w-5 text-success" /> : <ToggleLeft className="h-5 w-5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditLoyalty(p)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteLoyalty(p.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* COUPONS TAB */}
          <TabsContent value="coupons" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display font-semibold">Cupons de Desconto</h2>
                <p className="text-xs text-muted-foreground">Crie cupons com percentual, valor fixo, frete grátis ou produto grátis</p>
              </div>
              <Button onClick={openNewCoupon} className="bg-gradient-primary text-primary-foreground gap-2">
                <Plus className="h-4 w-4" /> Novo Cupom
              </Button>
            </div>

            {coupons.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-gradient-card p-12 text-center">
                <Ticket className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">Nenhum cupom cadastrado</p>
                <p className="text-xs text-muted-foreground mt-1">Crie cupons para atrair novos clientes</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {coupons.map((c) => {
                  const ct = COUPON_TYPE_LABELS[c.type];
                  const expired = c.valid_until && new Date(c.valid_until) < new Date();
                  const exhausted = c.max_uses !== null && c.uses_count >= c.max_uses;
                  return (
                    <motion.div
                      key={c.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-xl border bg-gradient-card shadow-card p-4 transition-all ${
                        c.is_active && !expired && !exhausted ? "border-primary/20" : "border-border opacity-60"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copyCode(c.code)}
                            className="font-mono text-lg font-bold tracking-wider bg-secondary px-3 py-1 rounded-lg hover:bg-secondary/80 transition-colors flex items-center gap-2"
                          >
                            {c.code}
                            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => toggleCoupon(c)}>
                            {c.is_active ? <ToggleRight className="h-4 w-4 text-success" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditCoupon(c)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCoupon(c.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-[10px]">{ct.label}</Badge>
                        {c.type === "percentage" && <span className="text-sm font-bold text-primary">{c.value}% OFF</span>}
                        {c.type === "fixed" && <span className="text-sm font-bold text-primary">{formatCurrency(c.value)} OFF</span>}
                        {c.type === "free_shipping" && <span className="text-sm font-bold text-primary">Frete Grátis</span>}
                        {c.type === "free_product" && <span className="text-sm font-bold text-primary">Produto Grátis</span>}
                        {expired && <Badge variant="destructive" className="text-[10px]">Expirado</Badge>}
                        {exhausted && <Badge variant="secondary" className="text-[10px]">Esgotado</Badge>}
                      </div>

                      <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                        {c.min_order_value > 0 && <span>Pedido mín. {formatCurrency(c.min_order_value)}</span>}
                        <span>Usado {c.uses_count}{c.max_uses ? `/${c.max_uses}` : ""} vezes</span>
                        {c.valid_until && (
                          <span>Até {new Date(c.valid_until).toLocaleDateString("pt-BR")}</span>
                        )}
                      </div>
                      {c.description && <p className="text-xs text-muted-foreground mt-2">{c.description}</p>}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Loyalty Program Dialog */}
      <Dialog open={loyaltyOpen} onOpenChange={setLoyaltyOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingProgram ? "Editar" : "Novo"} Programa de Fidelidade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingProgram && (
              <div>
                <Label className="text-xs">Tipo de programa</Label>
                <Select value={lpType} onValueChange={(v) => {
                  setLpType(v as any);
                  if (!lpName) {
                    setLpName(v === "points" ? "Programa de Pontos" : v === "stamp" ? "Cartão Fidelidade" : "Cashback");
                  }
                }}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="points">⭐ Programa de Pontos</SelectItem>
                    <SelectItem value="stamp">🎫 Cartão Fidelidade (Carimbos)</SelectItem>
                    <SelectItem value="cashback">💰 Cashback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs">Nome do programa *</Label>
              <Input value={lpName} onChange={(e) => setLpName(e.target.value)} placeholder="Ex: Clube de Pontos" className="mt-1" />
            </div>

            <div>
              <Label className="text-xs">Descrição (opcional)</Label>
              <Textarea value={lpDesc} onChange={(e) => setLpDesc(e.target.value)} placeholder="Ex: Acumule pontos e ganhe descontos!" className="mt-1" rows={2} />
            </div>

            {/* Type-specific config */}
            {lpType === "points" && (
              <div className="space-y-3 rounded-lg border border-border p-4">
                <h4 className="text-sm font-semibold flex items-center gap-2"><Star className="h-4 w-4 text-primary" /> Configuração de Pontos</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Pontos por R$ gasto</Label>
                    <Input type="number" min={0.1} step="0.1" value={lpPointsPerReal} onChange={(e) => setLpPointsPerReal(Number(e.target.value))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Pontos para resgatar</Label>
                    <Input type="number" min={1} value={lpPointsToRedeem} onChange={(e) => setLpPointsToRedeem(Number(e.target.value))} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Valor do desconto ao resgatar (R$)</Label>
                  <Input type="number" min={0.01} step="0.01" value={lpRewardValue} onChange={(e) => setLpRewardValue(Number(e.target.value))} className="mt-1" />
                </div>
                <p className="text-[11px] text-muted-foreground bg-secondary/50 p-2 rounded">
                  Exemplo: Cliente gasta R$ 100 → ganha {100 * lpPointsPerReal} pts. Com {lpPointsToRedeem} pts ganha {formatCurrency(lpRewardValue)} de desconto.
                </p>
              </div>
            )}

            {lpType === "stamp" && (
              <div className="space-y-3 rounded-lg border border-border p-4">
                <h4 className="text-sm font-semibold flex items-center gap-2"><Stamp className="h-4 w-4 text-primary" /> Configuração de Carimbos</h4>
                <div>
                  <Label className="text-xs">Carimbos necessários</Label>
                  <Input type="number" min={2} value={lpStampsRequired} onChange={(e) => setLpStampsRequired(Number(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Recompensa</Label>
                  <Input value={lpStampReward} onChange={(e) => setLpStampReward(e.target.value)} placeholder="Ex: 1 Pizza grátis" className="mt-1" />
                </div>
                <p className="text-[11px] text-muted-foreground bg-secondary/50 p-2 rounded">
                  Exemplo: Compre {lpStampsRequired} vezes → ganhe {lpStampReward}
                </p>
              </div>
            )}

            {lpType === "cashback" && (
              <div className="space-y-3 rounded-lg border border-border p-4">
                <h4 className="text-sm font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Configuração de Cashback</h4>
                <div>
                  <Label className="text-xs">Percentual de cashback (%)</Label>
                  <Input type="number" min={0.1} max={100} step="0.1" value={lpCashbackPct} onChange={(e) => setLpCashbackPct(Number(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Valor mínimo do pedido (R$)</Label>
                  <Input type="number" min={0} step="0.01" value={lpMinOrder} onChange={(e) => setLpMinOrder(Number(e.target.value))} className="mt-1" />
                </div>
                <p className="text-[11px] text-muted-foreground bg-secondary/50 p-2 rounded">
                  Exemplo: Cliente gasta R$ 50 → recebe {formatCurrency(50 * lpCashbackPct / 100)} de volta como crédito.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoyaltyOpen(false)}>Cancelar</Button>
            <Button onClick={saveLoyalty} disabled={saving || !lpName.trim()} className="bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingCoupon ? "Editar" : "Novo"} Cupom
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Código do cupom *</Label>
              <div className="flex gap-2 mt-1">
                <Input value={cpCode} onChange={(e) => setCpCode(e.target.value.toUpperCase())} placeholder="EX: PROMO10" className="font-mono tracking-wider" />
                <Button variant="outline" size="sm" onClick={() => setCpCode(generateCode())} title="Gerar código">
                  🎲
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Tipo de desconto</Label>
              <Select value={cpType} onValueChange={(v) => setCpType(v as any)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentual (%)</SelectItem>
                  <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                  <SelectItem value="free_shipping">Frete grátis</SelectItem>
                  <SelectItem value="free_product">Produto grátis</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(cpType === "percentage" || cpType === "fixed") && (
              <div>
                <Label className="text-xs">{cpType === "percentage" ? "Percentual de desconto (%)" : "Valor do desconto (R$)"}</Label>
                <Input type="number" min={0.01} step="0.01" value={cpValue} onChange={(e) => setCpValue(Number(e.target.value))} className="mt-1" />
              </div>
            )}

            {cpType === "free_product" && (
              <div>
                <Label className="text-xs">Produto grátis</Label>
                <Select value={cpProductId} onValueChange={setCpProductId}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o produto" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(Number(p.price))}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Pedido mínimo (R$)</Label>
                <Input type="number" min={0} step="0.01" value={cpMinOrder} onChange={(e) => setCpMinOrder(Number(e.target.value))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Limite de usos total</Label>
                <Input type="number" min={1} value={cpMaxUses} onChange={(e) => setCpMaxUses(e.target.value)} placeholder="Ilimitado" className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Uso máx. por cliente</Label>
                <Input type="number" min={1} value={cpMaxPerCustomer} onChange={(e) => setCpMaxPerCustomer(Number(e.target.value))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Válido até</Label>
                <Input type="date" value={cpValidUntil} onChange={(e) => setCpValidUntil(e.target.value)} className="mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Descrição (opcional)</Label>
              <Textarea value={cpDesc} onChange={(e) => setCpDesc(e.target.value)} placeholder="Ex: Cupom de boas-vindas para novos clientes" className="mt-1" rows={2} />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label className="text-xs font-medium">Aplicar em produtos com desconto</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">Se desativado, o cupom não será aceito quando o carrinho tiver produtos em promoção</p>
              </div>
              <Switch checked={cpAppliesToDiscounted} onCheckedChange={setCpAppliesToDiscounted} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponOpen(false)}>Cancelar</Button>
            <Button onClick={saveCoupon} disabled={saving || !cpCode.trim()} className="bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fidelidade;
